import React, { useState, useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Collapse,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import "./Drawer.css";
import logoTransmex from "../../assets/TransMexLogo-Final_CMYK.png";
import logoSwift from "../../assets/swift_Logo.png";
import ImagenPerfil from "../../assets/ImagenPerfilTransmex.png";
import Icons from "../../Utils/Icons";
import { getAssetUrl } from "../../Utils/assetUrl";

const DRAWER_WIDTH = 280;

const getUserDisplayName = (user) => {
  const emp = user?.idEmployeNavigation;
  const parts = [
    user?.name,
    emp?.lastName ?? emp?.LastName,
    emp?.secondLastName ?? emp?.SecondLastName,
  ].filter(Boolean);
  return parts.join(" ").trim() || user?.email || "";
};

export default function Drawers({
  user,
  drawerOpen,
  config,
  permissions,
}) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const theme = useTheme();
  const [openSectionIndex, setOpenSectionIndex] = useState(null);
  const [itemsPermissions, setItemsPermissions] = useState([]);

  const primary = theme.palette.primary.main;
  const idEmpresa = localStorage.getItem("empresa");
  const logoSrc = idEmpresa === "2" ? logoSwift : logoTransmex;

  const profileImageSrc = useMemo(() => {
    const url = getAssetUrl(config, "userImage", user?.user?.foto);
    return url || ImagenPerfil;
  }, [config, user?.user?.foto]);

  useEffect(() => {
    if (permissions?.length > 0) {
      setItemsPermissions(permissions);
    } else if (user?.user?.permisosWebUsuarios) {
      setItemsPermissions(
        user.user.permisosWebUsuarios.filter((permission) => permission?.estatus)
      );
    }
  }, [user, permissions]);

  useEffect(() => {
    const handleLanguageChange = () => {
      setItemsPermissions((prev) => [...prev]);
    };
    i18n.on("languageChanged", handleLanguageChange);
    return () => i18n.off("languageChanged", handleLanguageChange);
  }, [i18n]);

  const isRouteActive = (controlador) => {
    if (!controlador) return false;
    const path = location.pathname.toLowerCase();
    const segment = `/${String(controlador).toLowerCase()}`;
    return path === segment || path.startsWith(`${segment}/`);
  };

  const isHomeActive = useMemo(
    () => location.pathname.split("/").filter(Boolean).length === 0,
    [location.pathname]
  );

  useEffect(() => {
    const activeParentIndex = itemsPermissions.findIndex((permiso) => {
      const children =
        permiso.idPermisoWebNavigation?.inverseIdPermisoPadreNavigation ?? [];
      return children.some(
        (child) =>
          child.mostrarEnMenu &&
          child.estatus &&
          isRouteActive(child.controlador)
      );
    });
    if (activeParentIndex >= 0) {
      setOpenSectionIndex(activeParentIndex);
    }
  }, [location.pathname, itemsPermissions]);

  const getTranslatedDescription = (permiso) => {
    const nav = permiso.idPermisoWebNavigation;
    if (i18n.language?.startsWith("es")) {
      return (
        permiso.descripcion ||
        permiso.Descripcion ||
        nav?.descripcion ||
        nav?.Descripcion
      );
    }
    return (
      permiso.descripcionOtroLenguaje ||
      permiso.DescripcionOtroLenguaje ||
      nav?.descripcionOtroLenguaje ||
      nav?.DescripcionOtroLenguaje ||
      permiso.descripcion ||
      permiso.Descripcion ||
      nav?.descripcion ||
      nav?.Descripcion
    );
  };

  const getTranslatedActionDescription = (permiso) => {
    if (i18n.language?.startsWith("es")) {
      return (
        permiso.descripcionAccion ||
        permiso.DescripcionAccion ||
        permiso.idPermisoWebNavigation?.descripcionAccion ||
        permiso.idPermisoWebNavigation?.DescripcionAccion
      );
    }
    return (
      permiso.descripcionAccionOtroLenguaje ||
      permiso.DescripcionAccionOtroLenguaje ||
      permiso.idPermisoWebNavigation?.descripcionAccionOtroLenguaje ||
      permiso.idPermisoWebNavigation?.DescripcionAccionOtroLenguaje ||
      permiso.descripcionAccion ||
      permiso.DescripcionAccion
    );
  };

  const accentColor = "#31CDE8";

  const getMenuItemSx = ({ active = false, parentActive = false, submenu = false } = {}) => {
    return {
      position: "relative",
      borderRadius: submenu ? 1 : 2.5,
      mb: submenu ? 0.2 : 0.35,
      mx: submenu ? 0.25 : 0.75,
      py: submenu ? 0.65 : 1,
      pl: submenu ? 1.75 : 1.25,
      pr: submenu ? 1.25 : 1.5,
      minHeight: submenu ? 34 : 44,
      color: active
        ? "#fff"
        : parentActive
          ? "rgba(255,255,255,0.95)"
          : "rgba(255,255,255,0.72)",
      bgcolor: "transparent",
      border: "none",
      overflow: "hidden",
      transition: "all 0.22s ease",
      "&.Mui-selected": {
        bgcolor: "transparent",
      },
      "&.Mui-selected:hover": {
        bgcolor: "transparent",
      },
      ...(active &&
        submenu && {
          bgcolor: "rgba(49, 205, 232, 0.18)",
          background: "rgba(49, 205, 232, 0.18)",
          "&::before": {
            content: '""',
            position: "absolute",
            left: 0,
            top: 4,
            bottom: 4,
            width: 3,
            borderRadius: "2px",
            bgcolor: accentColor,
          },
        }),
      ...(active &&
        !submenu && {
          background:
            "linear-gradient(90deg, rgba(49, 205, 232, 0.3) 0%, rgba(7, 70, 142, 0.1) 65%, transparent 100%)",
          "&::before": {
            content: '""',
            position: "absolute",
            left: 0,
            top: "50%",
            transform: "translateY(-50%)",
            width: 4,
            height: "70%",
            borderRadius: 4,
            bgcolor: accentColor,
            boxShadow: `0 0 12px ${accentColor}99`,
          },
        }),
      ...(parentActive &&
        !active && {
          background:
            "linear-gradient(90deg, rgba(255,255,255,0.07) 0%, transparent 100%)",
        }),
      "&:hover": {
        color: "#fff",
        background: active
          ? submenu
            ? "rgba(49, 205, 232, 0.24)"
            : "linear-gradient(90deg, rgba(49, 205, 232, 0.36) 0%, rgba(7, 70, 142, 0.14) 65%, transparent 100%)"
          : "rgba(255,255,255,0.07)",
      },
    };
  };

  const iconBoxSx = (active) => ({
    width: 36,
    height: 36,
    borderRadius: 2,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    bgcolor: active ? accentColor : "rgba(255,255,255,0.1)",
    color: active ? "#fff" : "rgba(255,255,255,0.9)",
    minWidth: 36,
    boxShadow: active ? "0 4px 12px rgba(49, 205, 232, 0.35)" : "none",
    transition: "all 0.22s ease",
  });

  const renderMenuIcon = (iconName, active) => (
    <Box sx={iconBoxSx(active)}>
      <Icons
        iconName={iconName || "Folder"}
        fontSize="small"
        style={{ color: "inherit", padding: 0 }}
      />
    </Box>
  );

  const handleItemClick = (index) => {
    setOpenSectionIndex((prev) => (prev === index ? null : index));
  };

  const visiblePermissions = itemsPermissions.filter(
    (permiso) =>
      permiso.idPermisoWebNavigation?.mostrarEnMenu &&
      permiso.idPermisoWebNavigation?.estatus === true
  );

  return (
    <>
      <Drawer
        open={drawerOpen}
        variant="persistent"
        anchor="left"
        sx={{
          width: drawerOpen ? DRAWER_WIDTH : 0,
          flexShrink: 0,
          overflow: "hidden",
          transition: (muiTheme) =>
            muiTheme.transitions.create("width", {
              easing: muiTheme.transitions.easing.sharp,
              duration: muiTheme.transitions.duration.enteringScreen,
            }),
          "& .MuiDrawer-paper": {
            width: DRAWER_WIDTH,
            boxSizing: "border-box",
            border: "none",
            background: "transparent",
            overflow: "hidden",
          },
        }}
      >
        <Box className="drawer-container" role="presentation">
          <Box className="drawer-logo">
            <Box
              component={Link}
              to="/"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textDecoration: "none",
                width: "100%",
                height: "100%",
              }}
            >
              <Box
                component="img"
                alt={t("navigation.logoAlt")}
                src={logoSrc}
                sx={{
                  height: 40,
                  width: "auto",
                  maxWidth: "90%",
                  objectFit: "contain",
                }}
              />
            </Box>
          </Box>

          <Box className="drawer-profile">
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                p: 1.5,
                borderRadius: 2.5,
                bgcolor: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.1)",
              }}
            >
              <Box
                component="img"
                alt={t("navigation.profileAlt")}
                src={profileImageSrc}
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = ImagenPerfil;
                }}
                sx={{
                  width: 72,
                  height: 72,
                  borderRadius: "50%",
                  objectFit: "cover",
                  border: `3px solid ${primary}`,
                  boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
                }}
              />
              <Typography
                variant="subtitle1"
                fontWeight={700}
                align="center"
                sx={{ color: "#fff", mt: 1.5, lineHeight: 1.3 }}
              >
                {getUserDisplayName(user)}
              </Typography>
              <Typography
                variant="caption"
                align="center"
                sx={{ color: "rgba(255,255,255,0.65)", mt: 0.5 }}
              >
                {user?.email}
              </Typography>
            </Box>
          </Box>

          <Box className="drawer-scroll">
            <Typography
              component="span"
              className="drawer-menu-label"
              variant="caption"
            >
              {t("navigation.menu")}
            </Typography>
            <List className="drawer-menu" component="nav" disablePadding>
              <ListItemButton
                component={Link}
                to="/"
                selected={isHomeActive}
                sx={getMenuItemSx({ active: isHomeActive })}
              >
                <ListItemIcon sx={{ minWidth: 44 }}>
                  <Box sx={iconBoxSx(isHomeActive)}>
                    <HomeOutlinedIcon sx={{ fontSize: 20, color: "inherit" }} />
                  </Box>
                </ListItemIcon>
                <ListItemText
                  primary={t("navigation.home")}
                  primaryTypographyProps={{ fontWeight: isHomeActive ? 600 : 500 }}
                />
              </ListItemButton>

              {visiblePermissions.map((permiso, index) => {
                const nav = permiso.idPermisoWebNavigation;
                const children = (
                  nav?.inverseIdPermisoPadreNavigation ?? []
                ).filter((c) => c.mostrarEnMenu && c.estatus === true);
                const isOpen = openSectionIndex === index;
                const hasActiveChild = children.some((c) =>
                  isRouteActive(c.controlador)
                );

                return (
                  <React.Fragment key={`menu-${nav?.idPermisoWeb ?? index}`}>
                    <ListItemButton
                      onClick={() => handleItemClick(index)}
                      sx={getMenuItemSx({ parentActive: hasActiveChild })}
                    >
                      <ListItemIcon sx={{ minWidth: 44 }}>
                        {renderMenuIcon(nav?.icono, hasActiveChild)}
                      </ListItemIcon>
                      <ListItemText
                        primary={getTranslatedDescription(permiso)}
                        primaryTypographyProps={{
                          fontWeight: hasActiveChild ? 600 : 500,
                          fontSize: "0.9rem",
                        }}
                      />
                      {children.length > 0 &&
                        (isOpen ? (
                          <ExpandLessIcon sx={{ color: "rgba(255,255,255,0.6)" }} />
                        ) : (
                          <ExpandMoreIcon sx={{ color: "rgba(255,255,255,0.6)" }} />
                        ))}
                    </ListItemButton>

                    <Collapse in={isOpen} timeout="auto" unmountOnExit>
                      <List
                        disablePadding
                        sx={{
                          pl: 1,
                          pr: 0.5,
                          pb: 0.5,
                          ml: 2.75,
                          borderLeft: "2px solid rgba(255,255,255,0.1)",
                        }}
                      >
                        {children.map((inversePermiso, subIndex) => {
                          const subActive = isRouteActive(
                            inversePermiso.controlador
                          );
                          return (
                            <ListItemButton
                              key={`sub-${index}-${subIndex}`}
                              component={Link}
                              to={`/${inversePermiso.controlador}`}
                              selected={subActive}
                              sx={getMenuItemSx({
                                active: subActive,
                                submenu: true,
                              })}
                            >
                              <ListItemText
                                primary={getTranslatedActionDescription(
                                  inversePermiso
                                )}
                                primaryTypographyProps={{
                                  fontSize: "0.84rem",
                                  fontWeight: subActive ? 600 : 400,
                                  letterSpacing: subActive ? "0.01em" : 0,
                                }}
                              />
                            </ListItemButton>
                          );
                        })}
                      </List>
                    </Collapse>
                  </React.Fragment>
                );
              })}
            </List>
          </Box>

        </Box>
      </Drawer>
    </>
  );
}
